import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import {
  EXPEDIENTE_PADRAO, DIAS_DA_SEMANA, rotuloDoExpediente, validarFaixa,
} from '@/produtividade/expediente';

/**
 * ★★★ A-2 — O HORÁRIO DE UMA PESSOA, com ALMOÇO (decisão do dono, 10/out/2026).
 *
 * ⚠⚠ **Por que um diálogo e não campos na tabela:** são 7 dias × 4 horários = **28 campos**.
 * ★ Na linha da tabela eles comprimiriam as outras colunas para toda a equipe, inclusive para
 * quem nunca vai mexer em horário.
 *
 * ★★ **"Segue o horário da clínica" é o estado inicial, e ele é visível** — ⚠ foi a lição do
 * `noRodizio` da Damiana: um padrão implícito que ninguém vê na tela deixou uma SDR nova fora da
 * distribuição por dias, sem erro nenhum. ★ Aqui o padrão aparece escrito, com o horário que ele
 * resolve ao lado.
 *
 * ★ Só o gestor chega aqui (a tela Equipe já é dele), e as rules são a autoridade.
 */

const ROTULO = {
  seg: 'Segunda', ter: 'Terça', qua: 'Quarta', qui: 'Quinta',
  sex: 'Sexta', sab: 'Sábado', dom: 'Domingo',
};
/** A ordem da semana de trabalho: segunda primeiro, domingo por último. */
const ORDEM = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];
/** Datas de exemplo (uma de cada dia da semana) só para gerar o rótulo de pré-visualização. */
const EXEMPLO = {
  seg: '2026-10-12', ter: '2026-10-13', qua: '2026-10-14', qui: '2026-10-15',
  sex: '2026-10-16', sab: '2026-10-17', dom: '2026-10-18',
};

/** Tira `almocoDe`/`almocoAte` vazios: ★ o campo ausente é "sem intervalo", e `''` não é. */
const limpar = (faixa) => {
  if (!faixa) return null;
  const saida = { de: faixa.de, ate: faixa.ate };
  if (faixa.almocoDe && faixa.almocoAte) {
    saida.almocoDe = faixa.almocoDe;
    saida.almocoAte = faixa.almocoAte;
  }
  return saida;
};

export default function DialogoHorario({
  aberto, membro, expedienteDaClinica = EXPEDIENTE_PADRAO, aoFechar, aoSalvar,
}) {
  /** `null` = segue a clínica; objeto = horário próprio. */
  const [proprio, setProprio] = useState(null);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // ⚠ Reabrir o diálogo em OUTRA pessoa tem de recarregar: sem isto, o horário da Talita
  // apareceria no diálogo da Jéssica (e seria salvo nela).
  useEffect(() => {
    setProprio(membro?.expediente ?? null);
    setErro(null);
  }, [membro?.id, aberto]);

  const segueClinica = proprio === null;

  const mudar = (dia, parte, valor) =>
    setProprio((atual) => {
      const base = atual ?? expedienteDaClinica;
      const faixa = base[dia] ?? { de: '08:00', ate: '17:00' };
      return { ...base, [dia]: { ...faixa, [parte]: valor } };
    });

  const alternarDia = (dia, trabalha) =>
    setProprio((atual) => {
      const base = atual ?? expedienteDaClinica;
      return {
        ...base,
        [dia]: trabalha ? (expedienteDaClinica[dia] ?? EXPEDIENTE_PADRAO[dia] ?? { de: '08:00', ate: '17:00' }) : null,
      };
    });

  async function salvar() {
    setErro(null);
    if (!segueClinica) {
      // ⚠⚠ Valida DIA A DIA e nomeia o dia: "o almoço tem de ficar dentro do expediente" sem
      // dizer ONDE faria o gestor conferir sete linhas à mão.
      for (const dia of ORDEM) {
        const problema = validarFaixa(proprio[dia]);
        if (problema) {
          setErro(`${ROTULO[dia]}: ${problema}`);
          return;
        }
      }
    }
    setSalvando(true);
    try {
      const paraGravar = segueClinica
        ? null
        : Object.fromEntries(DIAS_DA_SEMANA.map((d) => [d, limpar(proprio[d])]));
      await aoSalvar(paraGravar);
      aoFechar();
    } catch {
      // ★ A mensagem de erro de permissão/rede é responsabilidade de quem chamou (a página
      // mostra na barra de erro dela); aqui só se destrava o botão.
      setErro('Não foi possível salvar o horário.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialogo titulo={`Horário — ${membro?.nome ?? ''}`} aberto={aberto} aoFechar={aoFechar} largura={620}>
      {erro && <p className="erro">{erro}</p>}

      <label className="campo-inline">
        <input
          type="checkbox"
          checked={segueClinica}
          onChange={(e) => setProprio(e.target.checked ? null : { ...expedienteDaClinica })}
        />
        {' '}Segue o horário da clínica
      </label>
      <p className="campo-ajuda">
        {segueClinica
          ? <>Hoje esta pessoa usa o horário da clínica: <strong>{rotuloDoExpediente(EXEMPLO.seg, expedienteDaClinica)}</strong> de segunda a sexta.</>
          : 'Horário próprio. Deixe o almoço em branco nos dias sem intervalo.'}
      </p>

      {!segueClinica && (
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Dia</th><th>Entra</th><th>Sai</th>
                <th>Almoço de</th><th>Almoço até</th><th>Trabalha?</th>
              </tr>
            </thead>
            <tbody>
              {ORDEM.map((dia) => {
                const faixa = proprio[dia];
                return (
                  <tr key={dia}>
                    <td>{ROTULO[dia]}</td>
                    <td>
                      <input type="time" value={faixa?.de ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — entra`}
                        onChange={(e) => mudar(dia, 'de', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.ate ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — sai`}
                        onChange={(e) => mudar(dia, 'ate', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.almocoDe ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — almoço de`}
                        onChange={(e) => mudar(dia, 'almocoDe', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.almocoAte ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — almoço até`}
                        onChange={(e) => mudar(dia, 'almocoAte', e.target.value)} />
                    </td>
                    <td>
                      <input type="checkbox" checked={Boolean(faixa)}
                        aria-label={`${ROTULO[dia]} — trabalha`}
                        onChange={(e) => alternarDia(dia, e.target.checked)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ★★ O EFEITO À VISTA: o mesmo rótulo que a pessoa vai ler no cartão de ritmo.
          ⚠ Sem isto, o gestor digita 28 campos e só descobre o resultado na tela da SDR. */}
      {!segueClinica && (
        <p className="campo-ajuda">
          Segunda: <strong>{rotuloDoExpediente(EXEMPLO.seg, proprio)}</strong>
          {' · '}Sábado: <strong>{rotuloDoExpediente(EXEMPLO.sab, proprio)}</strong>
        </p>
      )}

      <div className="dialogo-acoes">
        <button type="button" className="btn-secundario" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="btn-primario" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar horário'}
        </button>
      </div>
    </Dialogo>
  );
}
